/**
 * Разбор `HostContext.entry` — того, с чем платформа открывает модуль.
 *
 * Платформа открывает вью `productCard` и передаёт с ней идентификатор карточки
 * товара (`productId`) и `asin`. К `productId` привязывается история расчётов, по
 * `asin` считается сам товар. Хост может назвать и конкретный расчёт — `shareId`
 * (ссылка «Поделиться») или `calculationId` (запись истории товара): карточка
 * лаунча открывает калькулятор с тем просчётом, который к ней привязан, а не с
 * новым листом по карточке товара (Launch Pipeline, WS 15556998).
 *
 * Форма `payload` контрактом НЕ задана — `HostEntry` объявляет его как
 * `Record<string, unknown>`. Поэтому имена ключей читаются терпимо к написанию
 * (`productId` / `product_id` / `productID`), но значения НЕ угадываются: нет
 * распознаваемого ключа — нет и результата, о чём говорится в консоли. Это
 * нормализация чужого входа, а не подстановка дефолта: пустой `productId` увёл бы
 * историю расчётов к чужой карточке или в никуда.
 *
 * Вывод в консоль остаётся: по нему платформенная команда сверяет, что именно
 * доехало, и он же — единственный способ увидеть расхождение имён ключей до того,
 * как оно превратится в «модуль ничего не подставил».
 */

import { LOG_TAG } from '../shared/lib/logTag';
import { parseShareIdInput } from '../shared/lib/platformShareLink';
import type { LinkedCalculation, ProductEntry } from '../shared/lib/productEntry';
import type { HostContext, HostEntry } from './contract';

/**
 * Написания, в которых платформа может прислать идентификатор карточки.
 *
 * Список закрытый и короткий намеренно: он покрывает разницу соглашений об именах
 * (camelCase у фронта, snake_case у API), а не «любой ключ, похожий на id».
 */
const PRODUCT_ID_KEYS = ['productId', 'product_id', 'productID'] as const;
const ASIN_KEYS = ['asin', 'ASIN'] as const;
const SHARE_ID_KEYS = ['shareId', 'share_id'] as const;
const CALCULATION_ID_KEYS = ['calculationId', 'calculation_id'] as const;
// Карточка биржи поставщиков (WS 15587410, п.6): контракт тула на карточке биржи платформа
// ещё не зафиксировала — принимаем очевидные имена номера карточки.
const EXCHANGE_XID_KEYS = ['exchangeCardXid', 'exchangeXid', 'xid', 'cardNumber'] as const;
const EXCHANGE_XID_RE = /^\d{1,12}$/;

/** id записи истории: положительное целое — числом или его десятичной записью. */
const CALCULATION_ID_RE = /^[1-9][0-9]*$/;

export type { LinkedCalculation, ProductEntry };

function readString(payload: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return null;
}

/** Первое непустое значение из списка ключей — любого типа; разбирает его вызывающий. */
function readRaw(payload: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = payload[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return undefined;
}

function readCalculationId(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isSafeInteger(raw) && raw > 0 ? raw : null;
  if (typeof raw === 'string' && CALCULATION_ID_RE.test(raw.trim())) {
    const value = Number(raw.trim());
    return Number.isSafeInteger(value) ? value : null;
  }
  return null;
}

/**
 * Расчёт, который хост просит открыть, или `null`, если он не назван.
 *
 * Названный, но неразборчивый расчёт — `invalid`, а не `null`: `null` означает «открой
 * лист по карточке товара», и битый id тихо превратился бы в чужие числа на экране.
 * Оба ключа сразу — тоже `invalid`: какой из двух хотел показать хост, не угадываем.
 */
function readLinkedCalculation(payload: Record<string, unknown>): LinkedCalculation | null {
  const rawShare = readRaw(payload, SHARE_ID_KEYS);
  const rawCalculation = readRaw(payload, CALCULATION_ID_KEYS);
  if (rawShare !== undefined && rawCalculation !== undefined) {
    return { kind: 'invalid', reason: 'названы и shareId, и calculationId' };
  }
  if (rawShare !== undefined) {
    const shareId = typeof rawShare === 'string' ? parseShareIdInput(rawShare) : null;
    return shareId === null
      ? { kind: 'invalid', reason: `shareId не разобран: ${String(rawShare)}` }
      : { kind: 'share', shareId };
  }
  if (rawCalculation !== undefined) {
    const calculationId = readCalculationId(rawCalculation);
    return calculationId === null
      ? { kind: 'invalid', reason: `calculationId не разобран: ${String(rawCalculation)}` }
      : { kind: 'history', calculationId };
  }
  return null;
}

/**
 * Товар, с которым платформа открыла модуль, или `null`.
 *
 * `null` возвращается и когда `entry` не передан (standalone), и когда в payload нет
 * узнаваемого идентификатора. Различать эти случаи здесь незачем: и там, и там
 * привязывать историю не к чему, а причина попадает в консоль.
 */
export function readProductEntry(entry: HostEntry | undefined): ProductEntry | null {
  const payload = entry?.payload;
  if (!payload) return null;

  const productId = readString(payload, PRODUCT_ID_KEYS);
  if (productId === null) return null;

  return {
    productId,
    asin: readString(payload, ASIN_KEYS),
    calculation: readLinkedCalculation(payload),
  };
}

/**
 * Номер карточки биржи, с которой платформа открыла модуль, или `null`. Числом или
 * строкой из цифр; карточка товара (`productId`) сюда не относится.
 */
export function readExchangeEntry(entry: HostEntry | undefined): string | null {
  const payload = entry?.payload;
  if (!payload) return null;
  for (const key of EXCHANGE_XID_KEYS) {
    const value = payload[key];
    const text = typeof value === 'number' && Number.isInteger(value) ? String(value) : typeof value === 'string' ? value.trim() : '';
    if (EXCHANGE_XID_RE.test(text)) return text;
  }
  return null;
}

export interface EntrySummary {
  view: string;
  source: string;
  payloadKeys: string[];
  payload: Record<string, unknown> | null;
}

/**
 * Сводка по `entry` или `null`, если платформа его не передала.
 *
 * `null` — это НЕ ошибка: `entry` в контракте опционален, а в standalone его нет
 * вовсе. Отличать «не передали» от «передали пустое» важно ровно затем, чтобы не
 * искать причину не там.
 */
export function summarizeHostEntry(entry: HostEntry | undefined): EntrySummary | null {
  if (!entry) return null;
  const payload = entry.payload ?? null;
  return {
    view: entry.view,
    source: entry.source,
    payloadKeys: payload ? Object.keys(payload) : [],
    payload,
  };
}

/**
 * Печатает пришедшее в консоль страницы платформы.
 *
 * Возвращает сводку, а не `void`: так вызывающий код (и тест) видит, что именно
 * было показано, и проверка не сводится к «функция не упала».
 */
export function reportHostEntry(context: HostContext): EntrySummary | null {
  const summary = summarizeHostEntry(context.entry);

  if (summary === null) {
    console.info(`${LOG_TAG} entry не передан — модуль открыт без вью платформы`);
    return null;
  }

  console.info(
    `${LOG_TAG} entry: view=%s source=%s поля payload=[%s]`,
    summary.view,
    summary.source,
    summary.payloadKeys.join(', ') || '—',
  );
  // Отдельной строкой и объектом целиком: в DevTools он раскрывается деревом, а
  // интерполяция в строку схлопнула бы вложенность — ради которой всё и затевалось.
  console.info(`${LOG_TAG} payload:`, summary.payload);

  // Отдельно — распознан ли товар. Без этой строки расхождение имён ключей выглядит
  // как «модуль ничего не подставил», и искать причину будут в калькуляторе.
  const product = readProductEntry(context.entry);
  if (product === null) {
    console.warn(
      `${LOG_TAG} в payload нет идентификатора карточки (искали: %s) — ` +
        `автозаполнение и история по товару недоступны`,
      PRODUCT_ID_KEYS.join(' | '),
    );
  } else {
    console.info(`${LOG_TAG} товар: productId=%s asin=%s`, product.productId, product.asin ?? '—');
    if (product.calculation?.kind === 'invalid') {
      console.warn(`${LOG_TAG} расчёт для открытия назван, но не разобран: %s`, product.calculation.reason);
    } else if (product.calculation !== null) {
      console.info(`${LOG_TAG} открыть расчёт:`, product.calculation);
    }
  }

  return summary;
}
